import { Model } from 'app/model/backendmodel';
import { SapHanaLinkedService, hanaAuthTypes } from 'app/model/linkedservice/saphana.linkedservice';
import { AdapterRegistry } from '../adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from './linkedservice.adapter';
import { IModel } from 'app/model/base/annotation';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { passwordOrAKVtoSecretBase, akvToResource, secretBaseToPasswordOrAKV, akvToModel, parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from './azure-key-vault.util';
import { isString } from 'app/shared/utils/string.util';
import { Constants } from 'app/shared/shared-constants';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { AdapterContext } from 'shell-models-lib';
import { connectionStringPartToModel, connectionStringPartToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { ValueAdapter } from '../value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SapHanaLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: SapHanaLinkedService) => {
                    // when AKV is used for connection string, it is already well handled. So we don't need handle it.
                    // won't cover fully by json for now
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
                        if (ls.password instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                server: '*',
                authenticationType: '*',
                userName: '*',
                password: 'secret',
                encryptedCredential: '*'
            },
            parameters:'*'
        }
    }
})
export class SapHanaLinkedServiceAdapter extends LinkedServiceAdapter<SapHanaLinkedService> {
    protected supportFirstClassParameterExperience = true;
    protected modelConstructor(): new () => SapHanaLinkedService {
        return SapHanaLinkedService;
    }

    protected model(): IModel {
        return SapHanaLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: SapHanaLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SapHanaLinkedService;

        properties.type = Metamodel.modelFor(SapHanaLinkedService).name;

        // it's new create or existing odbc resource, check IR version
        if (!linkedService.isOldMashupResource) {
            linkedService.isMashupResource = false;

            if (!linkedService.enableAKV) {
                const builder = new DbConnectionStringBuilderService(true);
                builder.add('SERVERNODE', connectionStringPartToResource(linkedService, SapHanaLinkedService.SERVER));
                if (linkedService.authenticationType === hanaAuthTypes.Basic) {
                    builder.add('UID', connectionStringPartToResource(linkedService, SapHanaLinkedService.USER_NAME));
                    // handle akv seperately
                    if (!(linkedService.password instanceof AzureKeyVault) && linkedService.password) {
                        builder.add('PWD', connectionStringPartToResource(linkedService, SapHanaLinkedService.PASSWORD));
                    }
                }
                if (linkedService.additionalFields) {
                    linkedService.additionalFields.forEach((pair) => {
                        builder.add(pair.key, pair.value || '');
                    });
                }
                const connectionString = this._connectionStringToResource(builder.connectionString);

                properties.typeProperties = {
                    connectionString,
                    userName: linkedService.authenticationType === hanaAuthTypes.Windows ? this.valueToResource(linkedService, SapHanaLinkedService.USER_NAME) : undefined,
                    // when suth type is basic, password may be an akv
                    password: (linkedService.authenticationType === hanaAuthTypes.Windows) ? parameterizablePasswordOrAKVtoSecretBase(linkedService, SapHanaLinkedService.PASSWORD,this.context) as Model.SecretBase :
                        (linkedService.password instanceof AzureKeyVault) ? akvToResource(linkedService.password, this.context) : undefined
                };
            } else {
                properties.typeProperties = {
                    connectionString: akvToResource(linkedService.referedAKV, this.context)
                };
            }
            return linkedServiceResource;
        }
        properties.typeProperties = {
            server: linkedService.server,
            authenticationType: linkedService.authenticationType,
            userName: linkedService.userName,
            password: passwordOrAKVtoSecretBase(linkedService.password, this.context)
        };

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: SapHanaLinkedService): Promise<SapHanaLinkedService> {
        const prop = linkedServiceResource.properties as Model.SapHanaLinkedService;

        if (prop.typeProperties.connectionString == null) {
            linkedService.isOldMashupResource = true;
            linkedService.isMashupResource = true;
            linkedService.server = prop.typeProperties.server;
            linkedService.authenticationType = prop.typeProperties.authenticationType;
            linkedService.userName = prop.typeProperties.userName;
            linkedService.password = secretBaseToPasswordOrAKV(prop.typeProperties.password, true, this.context);
        } else {
            // for odbc resource
            linkedService.isOldMashupResource = false;
            linkedService.isMashupResource = false;
            linkedService.authenticationType = prop.typeProperties.userName ? hanaAuthTypes.Windows : hanaAuthTypes.Basic;

            if (isString(prop.typeProperties.connectionString)) {
                const builder = new DbConnectionStringBuilderService(true);
                builder.parse(prop.typeProperties.connectionString as string);
                connectionStringPartToModel(linkedService, SapHanaLinkedService.SERVER, builder.getValue('SERVERNODE', false) || '');
                connectionStringPartToModel(linkedService, SapHanaLinkedService.USER_NAME, builder.getValue('UID', false) || '');
                linkedService.password = Constants.maskedCredential;
                linkedService.additionalFields = builder.getRemainedKeyValuePairs(['SERVERNODE', 'UID', 'PWD']);

                // when auth type is basic, password may have akv
                if (linkedService.authenticationType === hanaAuthTypes.Basic && prop.typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, SapHanaLinkedService.PASSWORD, prop.typeProperties.password, this.context);
                }

            } else if (SecureStringReplacer.isSecureStringObject(prop.typeProperties.connectionString)) {
                linkedService.server = Constants.maskedCredential;
                linkedService.userName = Constants.maskedCredential;
                if (prop.typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, SapHanaLinkedService.PASSWORD, prop.typeProperties.password, this.context, false);
                } else {
                    linkedService.password = Constants.maskedCredential;
                }
            } else if ((prop.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.referedAKV = akvToModel(prop.typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
                linkedService.enableAKV = true;
            }
            if (linkedService.authenticationType === hanaAuthTypes.Windows) {
                ValueAdapter.toModel(linkedService, SapHanaLinkedService.USER_NAME, prop.typeProperties.userName);
               parameterizableSecretBaseToModel(linkedService, SapHanaLinkedService.PASSWORD, prop.typeProperties.password, this.context, true);
            }
        }

        return linkedService;
    }

    protected fullyByJsonEditorCustomizer(linkedServiceResource: Model.LinkedServiceResource): boolean {
        // if both password and linked service is an AKV, we should put it into JSON editor
        const prop = linkedServiceResource.properties as Model.SapHanaLinkedService;
        return prop?.typeProperties?.connectionString && (prop.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret' &&
            prop.typeProperties.password && (prop.typeProperties.password as Model.SecretBase).type === 'AzureKeyVaultSecret';
    }
}
